import React from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { cn } from '../../lib/utils';
import { TimesheetIssue } from '../../services/timesheetService';
import { formatHours } from './approvals.utils';

/** "6, 13, 20, 27 feb" — days of one month read as a list, the month named once at the end. */
export function formatIssueDates(dates: string[], locale: Locale): string {
  if (dates.length === 0) return '';
  const parsed = dates.map((date) => parseISO(date));
  const sameMonth = parsed.every((date) => date.getMonth() === parsed[0].getMonth() && date.getFullYear() === parsed[0].getFullYear());
  if (!sameMonth) return parsed.map((date) => format(date, 'd MMM', { locale })).join(', ');
  return `${parsed.map((date) => format(date, 'd')).join(', ')} ${format(parsed[0], 'MMM', { locale })}`;
}

const dotClass: Record<TimesheetIssue['severity'], string> = {
  error: 'bg-red-500',
  warning: 'bg-amber-500',
};

const chipClass: Record<TimesheetIssue['severity'], string> = {
  error: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300',
};

function useIssueText() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('it') ? it : enUS;
  const numberLocale = i18n.language || 'en';

  const label = (issue: TimesheetIssue) => t(`approvals.issues.codes.${issue.code}.label`, issue.code);
  const detail = (issue: TimesheetIssue) => t(`approvals.issues.codes.${issue.code}.detail`, '');
  const daysLabel = (count: number) =>
    count === 1 ? t('approvals.issues.dayOne', '1 giorno') : t('approvals.issues.dayOther', '{{count}} giorni', { count });

  /** Secondary facts for one issue: day count, the days themselves, hours, pending-leave hint. */
  const facts = (issue: TimesheetIssue): string[] => {
    const parts = [daysLabel(issue.dates.length), formatIssueDates(issue.dates, locale)];
    if (issue.hours && issue.hours > 0) parts.push(t('approvals.issues.hours', '{{hours}} h', { hours: formatHours(issue.hours, numberLocale) }));
    if (issue.pendingLeaveDays && issue.pendingLeaveDays > 0) {
      parts.push(t('approvals.issues.pendingLeave', '{{count}} con assenza in attesa di approvazione', { count: issue.pendingLeaveDays }));
    }
    return parts;
  };

  return { t, label, detail, facts };
}

interface IssueBadgesProps {
  issues: TimesheetIssue[];
  /** chips shown before folding the rest into "+N" */
  max?: number;
  className?: string;
}

/** Table cell: one chip per issue with its day count, the overflow folded into "+N". */
export function IssueBadges({ issues, max = 2, className }: IssueBadgesProps) {
  const { t, label, detail, facts } = useIssueText();

  if (issues.length === 0) {
    return (
      <span className={cn('text-muted-foreground/60', className)}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t('approvals.issues.none', 'Nessun problema rilevato')}</span>
      </span>
    );
  }

  const shown = issues.slice(0, max);
  const hidden = issues.slice(max);

  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      {shown.map((issue) => (
        <span
          key={issue.code}
          title={[detail(issue), ...facts(issue)].filter(Boolean).join(' · ')}
          className={cn('inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs', chipClass[issue.severity])}
        >
          <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClass[issue.severity])} />
          <span className="truncate">{label(issue)}</span>
          <span className="tabular-nums opacity-80">{issue.dates.length}</span>
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          title={hidden.map((issue) => `${label(issue)} ${issue.dates.length}`).join(' · ')}
          className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
        >
          {t('approvals.issues.more', '+{{count}}', { count: hidden.length })}
        </span>
      )}
    </span>
  );
}

interface IssueListProps {
  issues: TimesheetIssue[];
  className?: string;
}

/** Detail popup: every issue on its own line with the days it touches. */
export function IssueList({ issues, className }: IssueListProps) {
  const { t, label, detail, facts } = useIssueText();
  if (issues.length === 0) return null;

  const hasErrors = issues.some((issue) => issue.severity === 'error');

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        hasErrors
          ? 'border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/20'
          : 'border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20',
        className,
      )}
    >
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {t('approvals.issues.title', 'Problemi rilevati')} · {issues.length}
      </p>
      <ul className="space-y-1">
        {issues.map((issue) => (
          <li key={issue.code} className="flex items-baseline gap-2" title={detail(issue)}>
            <span aria-hidden="true" className={cn('mt-1 h-1.5 w-1.5 shrink-0 self-center rounded-full', dotClass[issue.severity])} />
            <span className="min-w-0">
              <span className="font-medium">{label(issue)}</span>
              <span className="text-muted-foreground"> · {facts(issue).join(' · ')}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
